import { useEffect, useState } from 'react';

import { currentSession, signOut, Session } from './auth';
import { useLoad } from './data';
import { Tabs } from './ui';
import { Account } from './types';
import { Join } from './screens/Join';
import { RedeemLink } from './screens/RedeemLink';
import { SignIn } from './screens/SignIn';
import { Alternatives } from './screens/hotel/Alternatives';
import { Calendar } from './screens/hotel/Calendar';
import { Dashboard } from './screens/hotel/Dashboard';
import { Guests } from './screens/hotel/Guests';
import { NewBooking } from './screens/hotel/NewBooking';
import { Products } from './screens/hotel/Products';
import { Register } from './screens/hotel/Register';
import { Reports } from './screens/hotel/Reports';
import { Suppliers } from './screens/hotel/Suppliers';
import { SupplierProfile } from './screens/hotel/SupplierProfile';
import { AdminConsole } from './screens/operator/AdminConsole';
import { Availability } from './screens/supplier/Availability';
import { Calendar as SupplierCalendar } from './screens/supplier/Calendar';
import { Confirmed } from './screens/supplier/Confirmed';
import { Earnings } from './screens/supplier/Earnings';
import { History } from './screens/supplier/History';
import { Hotels } from './screens/supplier/Hotels';
import { Profile } from './screens/supplier/Profile';
import { Requests } from './screens/supplier/Requests';
import { Reviews } from './screens/supplier/Reviews';
import { Today } from './screens/supplier/Today';
import { Trips } from './screens/supplier/Trips';
import { Eva } from './screens/supplier/Eva';

// The URL is the source of truth, in the estate's hash shape — a
// notification, an email or Eva can deep-link into any screen. Browser back
// always works, including closing a drawer. A query (`#/bookings?f=pending`)
// rides after the path and never leaks into the segments — filter states
// are URLs too, so an alert can open the exact view.
export function useHash(): string[] {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return (hash.replace(/^#\/?/, '').split('?')[0] ?? '').split('/').filter(Boolean);
}

// The hash's query half, read at call time — components re-render on
// hashchange via useHash, so reading live is safe.
export function hashQuery(): URLSearchParams {
  const i = window.location.hash.indexOf('?');
  return new URLSearchParams(i >= 0 ? window.location.hash.slice(i + 1) : '');
}

// A deep link hit without a session parks its target here; the correction
// branch after sign-in consumes it once. Tab-scoped by design.
const RETURN_TO = 'iso.returnTo';
function takeReturnTo(): string | null {
  const v = sessionStorage.getItem(RETURN_TO);
  sessionStorage.removeItem(RETURN_TO);
  return v;
}

export function go(path: string): void {
  window.location.hash = path.startsWith('#') ? path : `#${path}`;
}

// Redirects replace the history entry — back must land where the user was,
// never on a hash that instantly re-pushes forward.
export function goReplace(path: string): void {
  window.location.replace(path.startsWith('#') ? path : `#${path}`);
}

// The full rails — every canon 09 screen's module, as canon names them.
// Unbuilt modules are visible and quiet, never absent, tagged with the
// sprint that delivers them.
// The third element, when a screen has no path yet, is an HONEST note (shown on
// hover, badged "later") — not a sprint number that has long since passed. A
// quiet rail item is either genuinely deferred with a reason or it does not
// belong here. The canon-09 "Connections" register is NOT a quiet item: it is
// realised as the connection-state rows of the Suppliers directory (active /
// requested / invited), so it is not a second rail entry (DECISION-060). The
// register stays 41 — Connections is a state of Suppliers, the same way the Eva
// task list is a section of Today.
const HOTEL_RAIL: [string, string?, string?][] = [
  ['Dashboard', '/dashboard'],
  ['Bookings', '/bookings'],
  ['Calendar', '/calendar'],
  ['Products', '/products'],
  ['Suppliers', '/suppliers'],
  ['Guests', '/guests'],
  ['Messages', undefined, 'The comms-center (canon §8.2) — deferred, not in v1 (DECISION-060).'],
  ['Reports', '/reports'],
  ['Team & roles', undefined, 'Roles decided (DECISION-057); build deferred — group accounts still open (P3).'],
  ['Settings', undefined, 'Deferred to a later sprint.'],
];
const SUPPLIER_RAIL: [string, string?, string?][] = [
  ['Today', '/today'],
  ['Incoming requests', '/requests'],
  ['Confirmed', '/confirmed'],
  ['Availability', '/availability'],
  ['Booking history', '/history'],
  ['My hotels', '/hotels'],
  ['Business profile', '/profile'],
  ['Calendar', '/calendar'],
  ['Eva', '/eva'],
  ['Earnings', '/earnings'],
  ['Reviews', '/reviews'],
];

// The tab strip: a module's sub-views, underline marks the active one.
// Absent on single-view modules (canon), so only Bookings carries one today.
const HOTEL_TABS: Record<string, { label: string; path: string }[]> = {
  bookings: [
    { label: 'All bookings', path: '/bookings' },
    { label: 'Declined & alternatives', path: '/alternatives' },
  ],
  alternatives: [
    { label: 'All bookings', path: '/bookings' },
    { label: 'Declined & alternatives', path: '/alternatives' },
  ],
};

const HOTEL_PREFIXES = ['dashboard', 'bookings', 'alternatives', 'calendar', 'products', 'suppliers', 'guests', 'reports'];
const SUPPLIER_PREFIXES = ['today', 'requests', 'confirmed', 'calendar', 'availability', 'history', 'hotels', 'profile', 'trips', 'eva', 'earnings', 'reviews'];

export default function App() {
  const segments = useHash();
  const [session, setSession] = useState<Session | null>(currentSession());
  // B41: a magic link redeemed here hands over to the correction branch below,
  // which owns where a fresh session lands. Without this flag the link's route
  // kept rendering "Signing you in…" over a session that already existed.
  const [linkRedeemed, setLinkRedeemed] = useState(false);
  // The property record — name and timezone — from the API (DECISION-045).
  // The token's claims carry enough to render before it lands. NOT for an
  // operator: it is not a tenant, so GET /account 401s (resolveTenant rejects
  // it) and would bounce it straight back to sign-in — it has its own console.
  const account = useLoad<Account>(session && session.accountType !== 'operator' ? '/account' : null);

  // A 401 anywhere clears storage (api.ts signOut) — the in-memory session
  // must follow, or #/signin and the home redirect chase each other forever.
  useEffect(() => {
    if (session && !currentSession()) setSession(null);
  }, [segments, session]);

  // A magic link works pre-session by definition. useHash splits on '/', so
  // the query rides the second segment: ['auth', 'link?t=…'].
  if (segments[0] === 'auth' && segments[1] === 'link' && !linkRedeemed) {
    // No navigation here — the correction branch below owns where a fresh
    // session lands, including a parked deep-link target.
    return (
      <RedeemLink
        onSignedIn={(s) => {
          setSession(s);
          setLinkRedeemed(true);
        }}
      />
    );
  }

  // The invitation entry screen stands alone: the recipient has no account,
  // and whoever's session happens to be on this machine is not them.
  if (segments[0] === 'join') {
    // N40: the code that proves the phone signs the supplier in here — no
    // second sign-in — and their first Today is where setup starts.
    return (
      <Join
        onSignedIn={(s) => {
          setSession(s);
          go('/today');
        }}
      />
    );
  }

  if (!session) {
    // Park the deep target so sign-in can return to it — the SMS-code flow
    // navigates away from the URL, and a magic link never had it.
    if (segments.length > 0 && segments[0] !== 'signin') {
      sessionStorage.setItem(RETURN_TO, `/${segments.join('/')}${window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : ''}`);
    }
    return <SignIn segments={segments} onSignedIn={setSession} />;
  }

  // The ISO operator console (DECISION-059) — a third principal with its own
  // minimal shell, entirely off the two marketplace personas' rails. The
  // account-provisioning form is its first surface (the operator/billing console
  // the roadmap spine flagged). An operator reaches no hotel or supplier screen.
  if (session.accountType === 'operator') {
    return (
      <AdminConsole
        session={session}
        onSignOut={() => {
          signOut();
          setSession(null);
          go('/signin');
        }}
      />
    );
  }

  const isHotel = session.accountType === 'hotel';
  const home = isHotel ? '/dashboard' : '/today';
  const known = isHotel ? HOTEL_PREFIXES : SUPPLIER_PREFIXES;
  if (segments.length === 0 || segments[0] === 'signin' || segments[0] === 'auth' || !known.includes(segments[0]!)) {
    // The URL is the source of truth — an unknown or foreign path is
    // corrected, not silently rendered over. A parked deep-link target wins
    // over home, if it belongs to this role.
    const returnTo = takeReturnTo();
    const first = returnTo?.replace(/^\//, '').split(/[/?]/)[0] ?? '';
    goReplace(returnTo && known.includes(first) ? returnTo : home);
    return null;
  }
  // A routed screen is the deep target or supersedes it — nothing stays parked.
  sessionStorage.removeItem(RETURN_TO);

  const out = () => {
    signOut();
    setSession(null);
    go('/signin');
  };

  const rail = isHotel ? HOTEL_RAIL : SUPPLIER_RAIL;
  const tabs = isHotel ? HOTEL_TABS[segments[0]!] : undefined;
  const showTabs = tabs && !(segments[0] === 'bookings' && segments[1] === 'new');
  const property = account.data?.name ?? session.name;
  const { crumb, body } = route(session, segments);

  return (
    <div className="shell">
      <nav className="topnav">
        <span className="brandmark">I</span>
        <span className="crumb">
          {crumb.length > 1 && (
            <>
              <a className="parent" href={`#${crumb[0]!.path}`}>
                {crumb[0]!.label}
              </a>
              <span className="sep">/</span>
            </>
          )}
          <span className="here">{crumb[crumb.length - 1]!.label}</span>
        </span>
        <span className="right">
          <span className="property-pill">{property}</span>
          <span className="avatar">{session.name.slice(0, 1).toUpperCase()}</span>
          <button className="signout" onClick={out}>
            Sign out
          </button>
        </span>
      </nav>
      <div className="body-row">
        <aside className="rail">
          <div className="section-label">{isHotel ? 'Hotel' : 'Supplier'}</div>
          {rail.map(([label, path, note]) =>
            path ? (
              <a key={label} href={`#${path}`} className={active(segments, path) ? 'active' : ''}>
                {label}
              </a>
            ) : (
              // Deferred, with an honest reason on hover — never a stale "arrives
              // in sprint N" that already came and went (register honesty, F-216).
              <span key={label} className="soon" title={note}>
                {label}
                <span className="when">later</span>
              </span>
            ),
          )}
        </aside>
        <main className="content">
          {showTabs && (
            <Tabs
              items={tabs!.map((t) => ({
                label: t.label,
                href: `#${t.path}`,
                active: `/${segments[0]}` === t.path,
              }))}
            />
          )}
          <div className="content-inner">{body}</div>
        </main>
      </div>
    </div>
  );
}

function active(segments: string[], path: string): boolean {
  // Declined and alternatives is a screen of the Bookings module.
  if (path === '/bookings' && segments[0] === 'alternatives') return true;
  return `/${segments[0]}` === path && segments[1] !== 'new';
}

interface Crumb {
  label: string;
  path: string;
}

function route(
  session: Session,
  segments: string[],
): { crumb: Crumb[]; body: JSX.Element } {
  if (session.accountType === 'hotel') {
    if (segments[0] === 'bookings' && segments[1] === 'new') {
      return {
        crumb: [{ label: 'Bookings', path: '/bookings' }, { label: 'New booking', path: '/bookings/new' }],
        body: <NewBooking />,
      };
    }
    if (segments[0] === 'bookings') {
      return { crumb: [{ label: 'Bookings', path: '/bookings' }], body: <Register openId={segments[1]} /> };
    }
    if (segments[0] === 'suppliers') {
      // The invite form and a supplier's profile Record are both routes (every
      // screen has a URL). An id that isn't in the directory renders an honest
      // "not in your directory" state inside the Record, not a wrong-URL redirect.
      if (segments[1] === 'invite') {
        return {
          crumb: [{ label: 'Suppliers', path: '/suppliers' }, { label: 'Invite', path: '/suppliers/invite' }],
          body: <Suppliers invite />,
        };
      }
      if (segments[1]) {
        return {
          crumb: [{ label: 'Suppliers', path: '/suppliers' }, { label: 'Supplier', path: `/suppliers/${segments[1]}` }],
          body: <SupplierProfile accountId={segments[1]} />,
        };
      }
      return { crumb: [{ label: 'Suppliers', path: '/suppliers' }], body: <Suppliers /> };
    }
    if (segments[0] === 'alternatives') {
      // The compare view is a route, not a state flag — back walks
      // compare -> queue.
      return {
        crumb: [
          { label: 'Bookings', path: '/bookings' },
          { label: 'Declined & alternatives', path: '/alternatives' },
        ],
        body: <Alternatives openId={segments[1]} />,
      };
    }
    if (segments[0] === 'calendar') {
      return { crumb: [{ label: 'Calendar', path: '/calendar' }], body: <Calendar /> };
    }
    if (segments[0] === 'products') {
      // The Product editor is a route (#/products/{id}); browser back returns
      // to the catalogue. The editor 404s an unknown id in place.
      return {
        crumb: segments[1]
          ? [{ label: 'Products', path: '/products' }, { label: 'Product editor', path: `/products/${segments[1]}` }]
          : [{ label: 'Products', path: '/products' }],
        body: <Products editId={segments[1]} />,
      };
    }
    if (segments[0] === 'guests') {
      // The guest drawer is a route (#/guests/{name}); browser back closes it.
      return { crumb: [{ label: 'Guests', path: '/guests' }], body: <Guests openGuest={segments[1]} /> };
    }
    if (segments[0] === 'reports') {
      return { crumb: [{ label: 'Reports', path: '/reports' }], body: <Reports /> };
    }
    // The hotel home. (This case went missing in the Sprint 2 restyle and
    // /dashboard rendered the alternatives queue — caught in Sprint 3.)
    return { crumb: [{ label: 'Dashboard', path: '/dashboard' }], body: <Dashboard /> };
  }
  if (segments[0] === 'requests') {
    // The sheet and each of its states are routes, not state flags —
    // browser back walks decline -> sheet -> queue. A garbage third segment
    // is corrected to the sheet, never rendered under a wrong URL.
    if (segments[2] && segments[2] !== 'decline' && segments[2] !== 'propose') {
      goReplace(`/requests/${segments[1]}`);
    }
    const mode = segments[2] === 'decline' || segments[2] === 'propose' ? segments[2] : 'answer';
    return {
      crumb: [{ label: 'Incoming requests', path: '/requests' }],
      body: <Requests openId={segments[1]} mode={mode} />,
    };
  }
  if (segments[0] === 'confirmed') {
    // The detail drawer is a route (#/confirmed/{id}); browser back closes it.
    // A garbage id 404s in the drawer, indistinguishable from absence.
    return { crumb: [{ label: 'Confirmed', path: '/confirmed' }], body: <Confirmed openId={segments[1]} /> };
  }
  if (segments[0] === 'calendar') {
    // The supplier's own month grid (canon 09 supplier Calendar · Register).
    // The hotel branch above owns the hotel /calendar; a supplier session never
    // reaches it, so the two share a path without colliding.
    return { crumb: [{ label: 'Calendar', path: '/calendar' }], body: <SupplierCalendar /> };
  }
  if (segments[0] === 'availability') {
    return { crumb: [{ label: 'Availability', path: '/availability' }], body: <Availability /> };
  }
  if (segments[0] === 'history') {
    // The Booking-detail Record is a route (#/history/{id}); browser back closes it.
    return { crumb: [{ label: 'Booking history', path: '/history' }], body: <History openId={segments[1]} /> };
  }
  if (segments[0] === 'hotels') {
    return { crumb: [{ label: 'My hotels', path: '/hotels' }], body: <Hotels /> };
  }
  if (segments[0] === 'profile') {
    return { crumb: [{ label: 'Business profile', path: '/profile' }], body: <Profile /> };
  }
  if (segments[0] === 'trips') {
    // The supplier's own product editor is a route (#/trips, #/trips/new,
    // #/trips/{id}) reached from onboarding and Today — not a rail screen, so
    // the register stays closed at 41 (George's S11 ruling). Browser back returns.
    return {
      crumb: segments[1]
        ? [{ label: 'My trips', path: '/trips' }, { label: segments[1] === 'new' ? 'Add a trip' : 'Edit trip', path: `/trips/${segments[1]}` }]
        : [{ label: 'My trips', path: '/trips' }],
      body: <Trips editId={segments[1]} />,
    };
  }
  if (segments[0] === 'eva') {
    return { crumb: [{ label: 'Eva', path: '/eva' }], body: <Eva /> };
  }
  if (segments[0] === 'earnings') {
    return { crumb: [{ label: 'Earnings', path: '/earnings' }], body: <Earnings /> };
  }
  if (segments[0] === 'reviews') {
    // The reviews received (canon 09 Register, DECISION-056). Each row drills to
    // the shared Booking-detail Record (#/reviews/{id}); browser back closes it.
    return { crumb: [{ label: 'Reviews', path: '/reviews' }], body: <Reviews openId={segments[1]} /> };
  }
  return { crumb: [{ label: 'Today', path: '/today' }], body: <Today /> };
}
